import { useState, useEffect, useRef, useCallback } from 'react';

export function useSlideshowTimer(durationSeconds, imageCount) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const previousDuration = useRef(durationSeconds);
  const [timerResetToken, setTimerResetToken] = useState(0);

  useEffect(() => {
    // Reset to first image when duration changes (user changed interval)
    if (previousDuration.current !== durationSeconds) {
      setCurrentIndex(0);
      previousDuration.current = durationSeconds;
    }
  }, [durationSeconds]);

  useEffect(() => {
    // Don't start timer if no duration or no images
    if (!durationSeconds || imageCount === 0) {
      setCurrentIndex(0);
      return;
    }

    // Set up interval to advance images
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % imageCount);
    }, durationSeconds * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [durationSeconds, imageCount, timerResetToken]);

  // Jump to a specific image and restart interval countdown from that point.
  const jumpToIndex = useCallback((targetIndex) => {
    if (!Number.isInteger(targetIndex) || imageCount <= 0) {
      return;
    }

    const normalizedIndex = ((targetIndex % imageCount) + imageCount) % imageCount;
    setCurrentIndex(normalizedIndex);
    setTimerResetToken((prev) => prev + 1);
  }, [imageCount]);

  return { currentIndex, jumpToIndex };
}
